import axios from 'axios';

const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

API.interceptors.request.use(config => {
  // Block all requests if session was kicked
  if (localStorage.getItem('session_kicked') === '1') {
    const controller = new AbortController();
    controller.abort();
    return { ...config, signal: controller.signal };
  }
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

API.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401 && localStorage.getItem('session_kicked') !== '1') {
      const detail = err.response?.data?.detail || '';
      localStorage.removeItem('auth_token');
      if (detail.includes('Session expired') || detail.includes('another device')) {
        localStorage.setItem('session_kicked', '1');
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login?kicked=1';
        }
      } else if (!window.location.pathname.startsWith('/login') && window.location.pathname !== '/') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export function resetSessionKick() {
  localStorage.removeItem('session_kicked');
}

// In-memory cache & in-flight request deduplication for high-speed instant navigation
const requestCache = new Map();
const inFlightRequests = new Map();

export function clearApiCache() {
  requestCache.clear();
  inFlightRequests.clear();
}

export function getApiCacheStats() {
  return {
    cachedRequests: requestCache.size,
    inFlightRequests: inFlightRequests.size,
  };
}

export function getBackendBaseUrl() {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const protocol = window.location.protocol;
    const envBackend = process.env.REACT_APP_BACKEND_URL ? process.env.REACT_APP_BACKEND_URL.replace(/\/+$/, '') : '';

    if (host === 'localhost' || host === '127.0.0.1') {
      return envBackend || `http://${host}:8000`;
    }
    // If accessing from another LAN IP or domain, use envBackend if not localhost
    if (envBackend && !envBackend.includes('localhost') && !envBackend.includes('127.0.0.1')) {
      return envBackend;
    }
    // Otherwise point to backend on the same host with port 8000
    return `${protocol}//${host}:8000`;
  }
  return (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
}

export function resolveMediaUrl(url, msgId) {
  const backendBase = getBackendBaseUrl();

  // If no url or not string, there is no media
  if (!url || typeof url !== 'string' || !url.trim()) {
    return '';
  }

  const cleanUrl = url.trim();
  if (cleanUrl.startsWith('blob:') || cleanUrl.startsWith('data:')) return cleanUrl;

  // If url is a Meta lookaside or direct WhatsApp CDN URL that requires Bearer authentication
  const isMetaLookaside = cleanUrl.includes('lookaside.fbsbx.com') ||
                          cleanUrl.includes('fbcdn.net') ||
                          cleanUrl.includes('whatsapp.net') ||
                          cleanUrl.startsWith('?mid=') ||
                          cleanUrl.includes('mid=');
  if (isMetaLookaside) {
    if (msgId) {
      return backendBase ? `${backendBase}/api/whatsapp/messages/${msgId}/media` : `/api/whatsapp/messages/${msgId}/media`;
    }
    return '';
  }

  // If url has 127.0.0.1:8000 or localhost:8000
  if (cleanUrl.includes('127.0.0.1:8000') || cleanUrl.includes('localhost:8000')) {
    const rel = cleanUrl.replace(/^https?:\/\/[^/]+/, '');
    return backendBase ? `${backendBase}${rel}` : rel;
  }

  // If url contains /api/uploads/
  if (cleanUrl.includes('/api/uploads/')) {
    const rel = cleanUrl.slice(cleanUrl.indexOf('/api/uploads/'));
    return backendBase ? `${backendBase}${rel}` : rel;
  }
  // If url contains /uploads/
  if (cleanUrl.includes('/uploads/')) {
    const rel = cleanUrl.slice(cleanUrl.indexOf('/uploads/'));
    return backendBase ? `${backendBase}/api${rel}` : `/api${rel}`;
  }
  // If starts with /api/
  if (cleanUrl.startsWith('/api/')) {
    return backendBase ? `${backendBase}${cleanUrl}` : cleanUrl;
  }
  // If starts with relative /
  if (cleanUrl.startsWith('/')) {
    return backendBase ? `${backendBase}${cleanUrl}` : cleanUrl;
  }

  return cleanUrl;
}

const originalGet = API.get.bind(API);
API.get = function(url, config = {}) {
  // Allow explicit cache bypass
  if (config.noCache || config.headers?.['Cache-Control'] === 'no-cache') {
    return originalGet(url, config);
  }

  const key = `${url}?${JSON.stringify(config.params || {})}`;
  const now = Date.now();
  const cached = requestCache.get(key);

  // Chat message endpoints must NEVER be cached by the 10-second TTL to prevent cross-chat stale message leakage
  const isMessagesEndpoint = url.includes('/messages') || url.includes('/chat/messages') || url.includes('/bot-chat/') || url.includes('/chat/unified-users') || url.includes('/whatsapp/contacts');
  if (isMessagesEndpoint) {
    // Only deduplicate identical in-flight requests
    if (inFlightRequests.has(key)) {
      return inFlightRequests.get(key);
    }
    const promise = originalGet(url, config)
      .then(res => {
        inFlightRequests.delete(key);
        return res;
      })
      .catch(err => {
        inFlightRequests.delete(key);
        throw err;
      });
    inFlightRequests.set(key, promise);
    return promise;
  }

  // Polling endpoints have short deduplication window (1.5s), standard read endpoints have 10s TTL
  const isPolling = url.includes('/unread') || url.includes('/typing-status') || url.includes('/live-unread');
  const ttl = isPolling ? 1500 : 10000;

  if (cached && (now - cached.time < ttl)) {
    return Promise.resolve({ ...cached.res });
  }

  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key);
  }

  const promise = originalGet(url, config)
    .then(res => {
      inFlightRequests.delete(key);
      requestCache.set(key, { time: Date.now(), res });
      return res;
    })
    .catch(err => {
      inFlightRequests.delete(key);
      throw err;
    });

  inFlightRequests.set(key, promise);
  return promise;
};

// Any mutation invalidates read cache to guarantee data consistency
const wrapMutation = (origMethod) => {
  return function(...args) {
    clearApiCache();
    return origMethod(...args);
  };
};

API.post = wrapMutation(API.post.bind(API));
API.put = wrapMutation(API.put.bind(API));
API.delete = wrapMutation(API.delete.bind(API));
API.patch = wrapMutation(API.patch.bind(API));

export default API;
